import { isProduction } from 'common/utils/environment-util';
import { ROUTES_V2_PATHS } from 'constants/route-constants';
import Storage from 'grab-storage';
import { isAreaZoningError, isAreaZoningHardBlock } from 'helper/error-codes';
import { getPaxID, isPaxUser } from 'helper/grab-id-auth-helper';
import { getMexInfo, isFromMexApp } from 'helper/mex-app-helper';
import { getPrevSelectedCompanyID } from 'helper/user';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

const noop = () => {};

export const SCRIBE_PREV_STATE_STORAGE_KEY = '__SCRIBE_PREV_STATE_';

export const SCRIBE_CONFIG = {
  appName: 'express',
  custType: 'pax',
};

export const SCRIBE_STATE_NAMES = {
  EXPRESS_HOMEPAGE_WEB: 'EXPRESS_HOMEPAGE_WEB',
  EXPRESS_BOOKINGS_WEB: 'EXPRESS_BOOKINGS_WEB',
  EXPRESS_POI_SEARCH_WEB: 'EXPRESS_POI_SEARCH_WEB',
  EXPRESS_SAVED_POI_DETAILS: 'EXPRESS_SAVED_POI_DETAILS',
  EXPRESS_DELIVERY_DETAILS_WEB: 'EXPRESS_DELIVERY_DETAILS_WEB',
  EXPRESS_BULK_UPLOAD_WEB: 'EXPRESS_BULK_UPLOAD_WEB',
  EXPRESS_BULK_UPLOAD_REVIEW_WEB: 'EXPRESS_BULK_UPLOAD_REVIEW_WEB',
  EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB:
    'EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB',
  EXPRESS_BULK_UPLOAD_BOOK_WEB: 'EXPRESS_BULK_UPLOAD_BOOK_WEB',
  EXPRESS_BULK_UPLOAD_EDIT_ADDRESS_WEB: 'EXPRESS_BULK_UPLOAD_EDIT_ADDRESS_WEB',
  EXPRESS_BULK_UPLOAD_ERROR_PAGE: 'EXPRESS_BULK_UPLOAD_ERROR_PAGE',
  EXPRESS_DELIVERY_OPTIONS: 'EXPRESS_DELIVERY_OPTIONS',
  EXPRESS_NAVIGATION: 'EXPRESS_NAVIGATION',
  EXPRESS_REVIEW_OFFER_LIST: 'EXPRESS_REVIEW_OFFER_LIST',
  EXPRESS_OFFER_DETAILS: 'EXPRESS_OFFER_DETAILS',
  EXPRESS_ENTER_CODE_DETAILS: 'EXPRESS_ENTER_CODE_DETAILS',
};

export const SCRIBE_SHOPIFY_STATES = {
  EXPRESS_SHOPIFY_ORDERS_WEB: 'EXPRESS_SHOPIFY_ORDERS_WEB',
  EXPRESS_SHOPIFY_ADDRESS_HOVER_WEB: 'EXPRESS_SHOPIFY_ADDRESS_HOVER_WEB',
  EXPRESS_SHOPIFY_SETTINGS_WEB: 'EXPRESS_SHOPIFY_SETTINGS_WEB',
  EXPRESS_SHOPIFY_SETTINGS_UNLINK_WEB: 'EXPRESS_SHOPIFY_SETTINGS_UNLINK_WEB',
  EXPRESS_SHOPIFY_ONBOARD_PICKUP_WEB: 'EXPRESS_SHOPIFY_ONBOARD_PICKUP_WEB',
  EXPRESS_SHOPIFY_ONBOARD_MULTISTOP_WEB:
    'EXPRESS_SHOPIFY_ONBOARD_MULTISTOP_WEB',
  EXPRESS_SHOPIFY_VERIFY_ADDRESS_WEB: 'EXPRESS_SHOPIFY_VERIFY_ADDRESS_WEB',
  EXPRESS_SHOPIFY_ONBOARD_HOVER_ITEM_WEB:
    'EXPRESS_SHOPIFY_ONBOARD_HOVER_ITEM_WEB',
  EXPRESS_SHOPIFY_ONBOARD_HOVER_ORDER_WEB:
    'EXPRESS_SHOPIFY_ONBOARD_HOVER_ORDER_WEB',
  EXPRESS_SHOPIFY_ORDER_NUMBER_ITEMS_WEB:
    'EXPRESS_SHOPIFY_ORDER_NUMBER_ITEMS_WEB',
  EXPRESS_SHOPIFY_WELCOME_GO_SETTINGS_WEB:
    'EXPRESS_SHOPIFY_WELCOME_GO_SETTINGS_WEB',
};

export const SCRIBE_MEX_APP_STATES = {
  EXPRESS_MEX_APP_LOGIN: 'EXPRESS_MEX_APP_LOGIN',
};

// https://docs.google.com/spreadsheets/d/1Etup2bp--7Iucu7Ma9xUS9CwobH4FDxGdgvhUQ6LxXE
export const SCRIBE_EVENT_PARAMS = {
  STATE_NAME: 'STATE_NAME',
  PREVIOUS_STATE: 'PREVIOUS_STATE',
  COMPANY_ID: 'COMPANY_ID',
  PAX_ID: 'PAX_ID',
  LOGIN_TYPE: 'LOGIN_TYPE',
  DELIVERY_UUID: 'DELIVERY_UUID',
  ORDER_ID: 'ORDER_ID',
  BOOKING_CODE: 'BOOKING_CODE',
  STEP_TYPE: 'STEP_TYPE',
  PICKUP_POI_ID: 'PICKUP_POI_ID',
  DROPOFF_POI_ID: 'DROPOFF_POI_ID',
  SERVICE_TYPE: 'SERVICE_TYPE',
  SERVICE_TYPE_LIST: 'SERVICE_TYPE_LIST',
  ONGOING_BOOKINGS: 'ONGOING_BOOKINGS', // Return number of ongoing_bookings
  PAST_BOOKINGS: 'PAST_BOOKINGS',
  TAXI_TYPE: 'TAXI_TYPE', // [int] ID of taxi type chosem
  TAXI_TYPE_LIST: 'TAXI_TYPE_LIST',
  FARE_LIST: 'FARE_LIST',
  FARE: 'FARE',
  CITY_ID: 'CITY_ID',
  CITY_CODE: 'CITY_CODE',
  BOOKING_MODE: 'BOOKING_MODE',
  SEARCH_TEXT: 'SEARCH_TEXT',
  COUNT_DROPOFFS: 'COUNT_DROPOFFS',
  DELIVERY_NUMBER: 'DELIVERY_NUMBER',
  SENDER_NAME_TEXT: 'SENDER_NAME_TEXT',
  SENDER_PHONE_TEXT: 'SENDER_PHONE_TEXT',
  RECIPIENT_NAME_TEXT: 'RECIPIENT_NAME_TEXT',
  RECIPIENT_PHONE_TEXT: 'RECIPIENT_PHONE_TEXT',
  COD_AMOUNT: 'COD_AMOUNT', // [float] COD amount without currency sign
  ITEM_WEIGHT: 'ITEM_WEIGHT',
  CATEGORY_NAME: 'CATEGORY_NAME',
  TEXT: 'TEXT',
  IS_CASHLESS: 'IS_CASHLESS',
  PAYMENT_METHOD: 'PAYMENT_METHOD',
  PROMO_CODE: 'PROMO_CODE',
  HAS_PROMO_AVAILABLE: 'HAS_PROMO_AVAILABLE',
  HAS_PROMO_APPLIED: 'HAS_PROMO_APPLIED',
  TOTAL_DELIVERIES: 'TOTAL_DELIVERIES',
  INVALID_DELIVERIES: 'INVALID_DELIVERIES',
  CURRENCY: 'CURRENCY',
  ORIGINAL_AMOUNT: 'ORIGINAL_AMOUNT',
  ORIGINAL_COUNT: 'ORIGINAL_COUNT',
  OPTIMIZED_AMOUNT: 'OPTIMIZED_AMOUNT',
  OPTIMIZED_COUNT: 'OPTIMIZED_COUNT',
  BULK_DELIVERY_REFERENCE_CODE: 'BULK_DELIVERY_REFERENCE_CODE',
  IS_MULTISTOP_BOOKING: 'IS_MULTISTOP_BOOKING',
  ROUTE_OPTIMIZE_STATUS: 'ROUTE_OPTIMIZE_STATUS',
  BULK_UPLOAD_TOTAL: 'BULK_UPLOAD_TOTAL',
  BULK_DELIVERY_TOTAL: 'BULK_DELIVERY_TOTAL',
  UPLOADED_DISTRICT: 'UPLOADED_DISTRICT',
  BULK_EDIT_ORIGINAL: 'BULK_EDIT_ORIGINAL',
  BULK_EDIT_ADJUSTED: 'BULK_EDIT_ADJUSTED',
  BULK_UPLOAD_DISTRICT_PATH: 'BULK_UPLOAD_DISTRICT_PATH',
  BULK_UPLOAD_DISTRICT_SELECTION: 'BULK_UPLOAD_DISTRICT_SELECTION',
  BULK_BOOKING_NUMBER: 'BULK_BOOKING_NUMBER',
  CHARACTER_TYPED: 'CHARACTER_TYPED',
  POI_TYPE: 'POI_TYPE',
  POI_ID: 'POI_ID',
  SELECT_POI_INDEX: 'SELECT_POI_INDEX',
  RECOMMEND_POI_LIST: 'RECOMMEND_POI_LIST',
  VERSION: 'VERSION',
  ONGOING_FILTER_NAME: 'ONGOING_FILTER_NAME',
  LANGUAGE: 'LANGUAGE',
  ADD_ON_LIST: 'ADD_ON_LIST',
  BULK_SELECTED: 'BULK_SELECTED',
  ONGOING_BOOKINGS_FILTER: 'ONGOING_BOOKINGS_FILTER',
  IS_APPLIED_OFFER: 'IS_APPLIED_OFFER',
  APPLIED_OFFER_RESULT: 'APPLIED_OFFER_RESULT',
  TYPE_CODE: 'TYPE_CODE',
  IS_CODE_ERROR: 'IS_CODE_ERROR',
  IS_CODE_USED: 'IS_CODE_USED',
  BULK_SENDER_TOTAL: 'BULK_SENDER_TOTAL',
  BULK_DELIVERY_SENDER: 'BULK_DELIVERY_SENDER',
  BULK_UPLOAD_ERROR_TYPE: 'BULK_UPLOAD_ERROR_TYPE',
  BULK_UPLOAD_ERROR_CODE: 'BULK_UPLOAD_ERROR_CODE',
  BULK_UPLOAD_DISTRICT_USAGE: 'BULK_UPLOAD_DISTRICT_USAGE',
  BULK_UPLOAD_TOTAL_ADDR_COUNT: 'BULK_UPLOAD_TOTAL_ADDR_COUNT',
  POI_SELECT_TYPE: 'POI_SELECT_TYPE',
  PICKUP_POI_SRC: 'PICKUP_POI_SRC',
  IS_RECOMMENDED_POI: 'IS_RECOMMENDED_POI',
  POI_CATEGORY: 'POI_CATEGORY',
  POI_LABEL: 'POI_LABEL',
  LATITUDE: 'LATITUDE',
  LONGITUDE: 'LONGITUDE',
  DELIVER_TO_INDEX: 'DELIVER_TO_INDEX',
  SAVE_POI_NUMBER: 'SAVE_POI_NUMBER',
  FAVORITE_PREDICT_FLAG: 'FAVORITE_PREDICT_FLAG',
  DO_NOT_SHOW_AGAIN: 'DO_NOT_SHOW_AGAIN',
  IS_SAVED_POI: 'IS_SAVED_POI',
  EDIT_SAVED_POI_ORIGINAL: 'EDIT_SAVED_POI_ORIGINAL',
  EDIT_SAVED_POI_SAVE: 'EDIT_SAVED_POI_SAVE',
  EDIT_SAVED_POI_VALUE: 'EDIT_SAVED_POI_VALUE',
  ERROR_TYPE: 'ERROR_TYPE',
  TYPE_OF_CONTENT: 'TYPE_OF_CONTENT',
};

export const SCRIBE_REGULATION_EVENTS = {
  EV_REGULATION_BANNER_VIEW: 'EV_REGULATION_BANNER_VIEW',
  EV_REGULATION_SNACKBAR_VIEW: 'EV_REGULATION_SNACKBAR_VIEW',
  EV_REGULATION_PRICE_BREAKDOWN_ERROR_VIEW:
    'EV_REGULATION_PRICE_BREAKDOWN_ERROR_VIEW',
  EV_REGULATION_PRICE_BREAKDOWN_TOOLTIP_VIEW:
    'EV_REGULATION_PRICE_BREAKDOWN_TOOLTIP_VIEW',
  EV_REGULATION_PRICE_BREAKDOWN_TOOLTIP_DELETE:
    'EV_REGULATION_PRICE_BREAKDOWN_TOOLTIP_DELETE',
  EV_REGULATION_PRICE_BREAKDOWN_TOOLTIP_REFRESH:
    'EV_REGULATION_PRICE_BREAKDOWN_TOOLTIP_REFRESH',
};

export const ROUTE_OPTIMIZE_STATUS_VALUE = {
  OPTIMIZED_SUCCESS: 'optimized_success',
  DID_NOT_OPTIMIZE: 'did_not_optimize',
  ERROR: 'error',
};

export const SCRIBE_COMMON_EVENTS = {
  HOME: 'HOME',
  BOOKINGS: 'BOOKINGS',
  ADMIN: 'ADMIN',
  PROFILE: 'PROFILE',
  MOBILE_MENU: 'MOBILE_MENU',
  BACK: 'BACK',
  LANGUAGE: 'LANGUAGE',
  LOGOUT: 'LOGOUT',
};

// Define in which path the common events should be tracked & its corresponding state via a mapping
export const SCRIBE_COMMON_EVENTS_STATE_PATH_MAPPING = {
  HOME: {
    [ROUTES_V2_PATHS.bookings]: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
    [ROUTES_V2_PATHS.shopifyHome]:
      SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
  },
  BOOKINGS: {
    [ROUTES_V2_PATHS.home]: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
    [ROUTES_V2_PATHS.shopifyHome]:
      SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
  },
  ADMIN: {
    [ROUTES_V2_PATHS.home]: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
    [ROUTES_V2_PATHS.bookings]: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
    [ROUTES_V2_PATHS.shopifyHome]:
      SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
  },
  PROFILE: {
    [ROUTES_V2_PATHS.home]: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
    [ROUTES_V2_PATHS.bookings]: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
    [ROUTES_V2_PATHS.shopifyHome]:
      SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
  },
  MOBILE_MENU: {
    [ROUTES_V2_PATHS.home]: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
    [ROUTES_V2_PATHS.bookings]: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
  },
};

export const SCRIBE_HOME_EVENTS = {
  DEFAULT: 'DEFAULT',
  CITY_SELECTION: 'CITY_SELECTION',
  SINGLE_BOOKING: 'SINGLE_BOOKING',
  MULTIPLE_BOOKINGS: 'MULTIPLE_BOOKINGS',
  PICKUP_ADDRESS: 'PICKUP_ADDRESS',
  SWAP: 'SWAP',
  DROPOFF_ADDRESS: 'DROPOFF_ADDRESS',
  SERVICE_TYPE: 'SERVICE_TYPE',
  VIEW_ONGOING: 'VIEW_ONGOING',
  VIEW_PAST_BOOKINGS: 'VIEW_PAST_BOOKINGS',
  BOOK_AGAIN: 'BOOK_AGAIN',
  CONFIRM: 'CONFIRM',
  VIEW_UPCOMING: 'VIEW_UPCOMING',
  BULK_UPLOAD: 'BULK_UPLOAD',
  ADD_STOP: 'ADD_STOP',
  VIEW_RECENT_UPDATES: 'VIEW_RECENT_UPDATES',
  SELECT_POI_FROM_DELIVER_TO: 'SELECT_POI_FROM_DELIVER_TO',
};

export const SCRIBE_BOOKING_EVENTS = {
  DEFAULT: 'DEFAULT',
  SEARCH_INPUT: 'SEARCH_INPUT',
  SEARCH: 'SEARCH',
  ONGOING_BOOKINGS_STATUS: 'ONGOING_BOOKINGS_STATUS',
  ONGOING_BOOKINGS_DETAILS: 'ONGOING_BOOKINGS_DETAILS',
  SHOW_ONGOING_BOOKINGS: 'SHOW_ONGOING_BOOKINGS',
  PAST_BOOKINGS_STATUS: 'PAST_BOOKINGS_STATUS',
  DATE_RANGE: 'DATE_RANGE',
  PAST_BOOKINGS_CSV: 'PAST_BOOKINGS_CSV',
  PAST_BOOKINGS_DETAILS: 'PAST_BOOKINGS_DETAILS',
  ONGOING_FILTER_SELECT: 'ONGOING_FILTER_SELECT',
  BOOK_AGAIN: 'BOOK_AGAIN',
  PRINT_LABEL: 'PRINT_LABEL',
  BULK_PRINT_LABEL: 'BULK_PRINT_LABEL',
  ONGOING_BOOKINGS_FILTER: 'ONGOING_BOOKINGS_FILTER',
};

export const SCRIBE_DELIVERY_OPTIONS_EVENTS = {
  BACK: 'BACK',
  DELIVERY_TYPE: 'DELIVERY_TYPE',
  SELECT_PICKUP_DATE: 'SELECT_PICKUP_DATE',
  SELECT_PICKUP_TIME: 'SELECT_PICKUP_TIME',
  CONFIRM: 'CONFIRM',
};

export const SCRIBE_POI_SEARCH_EVENTS = {
  DEFAULT: 'DEFAULT',
  SELECT_POI_FROM_RESULT: 'SELECT_POI_FROM_RESULT',
  POI_SELECT: 'POI_SELECT',
  ADD_STOP: 'ADD_STOP',
  CONFIRM: 'CONFIRM',
  FIND_SHORTEST_ROUTE: 'FIND_SHORTEST_ROUTE',
  FIND_SHORTEST_ROUTE_RESULTS: 'FIND_SHORTEST_ROUTE_RESULTS',
  USE_ORIGINAL_ROUTE: 'USE_ORIGINAL_ROUTE',
  MAP_SEARCH_RESULT: 'MAP_SEARCH_RESULT',
};

export const SCRIBE_DELIVERY_DETAILS_EVENTS = {
  DEFAULT: 'DEFAULT',
  PICKUP_ADDRESS: 'PICKUP_ADDRESS',
  DROPOFF_ADDRESS: 'DROPOFF_ADDRESS',
  SENDER_NAME: 'SENDER_NAME',
  SENDER_PHONE: 'SENDER_PHONE',
  RECIPIENT_NAME: 'RECIPIENT_NAME',
  RECIPIENT_PHONE: 'RECIPIENT_PHONE',
  COD_VALUE: 'COD_VALUE',
  ADD_STOP: 'ADD_STOP',
  SERVICE_TYPE: 'SERVICE_TYPE',
  VEHICLE_TYPE: 'VEHICLE_TYPE',
  EDIT_DELIVERY: 'EDIT_DELIVERY',
  ADD_ON: 'ADD_ON',
  DELETE_ADD_ON: 'DELETE_ADD_ON',
  BIKE: 'BIKE',
  CAR: 'CAR',
  ITEM_WEIGHT: 'ITEM_WEIGHT',
  ITEM_CATEGORY: 'ITEM_CATEGORY',
  DRIVER_NOTES: 'DRIVER_NOTES',
  PAYMENT_TYPE: 'PAYMENT_TYPE',
  REF_CODE: 'REF_CODE',
  PROMO_CODE: 'PROMO_CODE',
  BOOK: 'BOOK',
  DUPLICATE_DELIVERY: 'DUPLICATE_DELIVERY',
  ADD_DELIVERY_BOTTOM: 'ADD_DELIVERY_BOTTOM',
  DELETE_DELIVERY: 'DELETE_DELIVERY',
  LEFT_BAR: 'LEFT_BAR',
  ADD_DELIVERY_LEFT: 'ADD_DELIVERY_LEFT',
  EXPAND: 'EXPAND',
  COLLAPSE: 'COLLAPSE',
  APPLY_OFFER: 'APPLY_OFFER',
};

export const SCRIBE_REVIEW_OFFER_LIST_EVENTS = {
  DEFAULT: 'DEFAULT',
  BACK: 'BACK',
  ENTER_OFFER_CODE: 'ENTER_OFFER_CODE',
  CHECK_OFFER_DETAILS: 'CHECK_OFFER_DETAILS',
  SELECT_OFFER: 'SELECT_OFFER',
  APPLY_OFFER: 'APPLY_OFFER',
  CONTINUE_WITHOUT_OFFER: 'CONTINUE_WITHOUT_OFFER',
};

export const SCRIBE_OFFER_DETAILS_EVENTS = {
  DEFAULT: 'DEFAULT',
  BACK: 'BACK',
  USE_OFFER: 'USE_OFFER',
};

export const SCRIBE_OFFER_ENTER_CODE_DETAILS = {
  BACK: 'BACK',
  TYPE_CODE: 'TYPE_CODE',
  SHOW_ERROR_RESULT: 'SHOW_ERROR_RESULT',
  SELECT_OFFER: 'SELECT_OFFER',
  APPLY_OFFER: 'APPLY_OFFER',
};

export const SCRIBE_BULK_UPLOAD_MODAL_EVENTS = {
  DEFAULT: 'DEFAULT',
  UPLOAD_CSV: 'UPLOAD_CSV',
  DOWNLOAD_TEMPLATE: 'DOWNLOAD_TEMPLATE',
};

export const SCRIBE_BULK_UPLOAD_REVIEW_EVENTS = {
  DEFAULT: 'DEFAULT',
  SELECT_COLUMNS: 'SELECT_COLUMNS',
  SELECT_STATUS: 'SELECT_STATUS',
  EDIT_ADDRESS: 'EDIT_ADDRESS',
  BOOK: 'BOOK',
  DELETE_DELIVERY: 'DELETE_DELIVERY',
};

export const SCRIBE_BULK_UPLOAD_EDIT_ADDRESS_EVENTS = {
  DEFAULT: 'DEFAULT',
  ADDRESS: 'ADDRESS',
  LOCATION_NOTES: 'LOCATION_NOTES',
  CANCEL: 'CANCEL',
  CONFIRM: 'CONFIRM',
};

export const SCRIBE_EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_EVENTS = {
  DEFAULT: 'DEFAULT',
  SELECT_ALL: 'SELECT_ALL',
  EDIT_SERVICE_TYPE: 'EDIT_SERVICE_TYPE',
  EDIT_VEHICLE_TYPE: 'EDIT_VEHICLE_TYPE',
  EDIT_ADD_ON: 'EDIT_ADD_ON',
  CONFIRM: 'CONFIRM',
  TOGGLE_MULTI_STOP_ON: 'TOGGLE_MULTI_STOP_ON',
  TOGGLE_MULTI_STOP_OFF: 'TOGGLE_MULTI_STOP_OFF',
  REVIEW_MULTI_STOP: 'REVIEW_MULTI_STOP',
  REVIEW_MULTI_STOP_SKIP: 'REVIEW_MULTI_STOP_SKIP',
  REVIEW_MULTI_STOP_ACCEPT: 'REVIEW_MULTI_STOP_ACCEPT',
  FIND_SHORTEST_ROUTE: 'FIND_SHORTEST_ROUTE',
  FIND_SHORTEST_ROUTE_RESULTS: 'FIND_SHORTEST_ROUTE_RESULTS',
  USE_ORIGINAL_ROUTE: 'USE_ORIGINAL_ROUTE',
};

export const SCRIBE_EXPRESS_BULK_UPLOAD_BOOK_WEB_EVENTS = {
  DEFAULT: 'DEFAULT',
  PAYMENT_METHOD: 'PAYMENT_METHOD',
  OFFERS: 'OFFERS',
  BOOK: 'BOOK',
};

export const SCRIBE_SHOPIFY_EVENTS = {
  DEFAULT: 'DEFAULT',

  // EXPRESS_SHOPIFY_ORDERS_WEB State
  SHOPIFY_ORDERS: 'SHOPIFY_ORDERS',
  SETTINGS: 'SETTINGS',
  STORE_NAME_DROPDOWN: 'STORE_NAME_DROPDOWN',
  STORE_NAME: 'STORE_NAME',
  LINK_NEW_STORE: 'LINK_NEW_STORE',
  SEARCH_ORDERS: 'SEARCH_ORDERS',
  PICKUP_POINT: 'PICKUP_POINT',
  DROPOFF_POINT: 'DROPOFF_POINT',
  MAP_ZOOM_IN: 'MAP_ZOOM_IN',
  MAP_ZOOM_OUT: 'MAP_ZOOM_OUT',
  MAP_CENTER: 'MAP_CENTER',
  FILTER_DATE: 'FILTER_DATE',
  FILTER_SHIPPING_TYPE: 'FILTER_SHIPPING_TYPE',
  REFRESH_ORDERS: 'REFRESH_ORDERS',
  SELECT_ALL: 'SELECT_ALL',
  SELECT_ORDER: 'SELECT_ORDER',
  BOOK_MULTISTOP_DELIVERY: 'BOOK_MULTISTOP_DELIVERY',
  BOOK_NORMAL_DELIVERY: 'BOOK_NORMAL_DELIVERY',
  MULTISTOP_DELIVERY_ERROR: 'MULTISTOP_DELIVERY_ERROR',
  DEFAULT_UNLINKED: 'DEFAULT_UNLINKED',
  LINK_URL: 'LINK_URL',
  LINK_URL_FAILURE: 'LINK_URL_FAILURE',
  LEARN_MORE: 'LEARN_MORE',

  // EXPRESS_DELIVERY_DETAILS_WEB
  SHOPIFY_ITEM_DETAIL: 'SHOPIFY_ITEM_DETAIL',
  CLICK_GROUP_ORDERS: 'CLICK_GROUP_ORDERS',
  BOOK: 'BOOK',
  EDIT_DELIVERY: 'EDIT_DELIVERY',

  // EXPRESS_SHOPIFY_ADDRESS_HOVER_WEB State
  ACCEPT_ADDRESS: 'ACCEPT_ADDRESS',
  EDIT_ADDRESS: 'EDIT_ADDRESS',

  // EXPRESS_SHOPIFY_SETTINGS_WEB State
  BACK_TO_SHOPIFY_ORDERS: 'BACK_TO_SHOPIFY_ORDERS',
  EDIT_STORE_URL: 'EDIT_STORE_URL',
  SAVE_STORE_URL: 'SAVE_STORE_URL',
  LINK_STORE_URL: 'LINK_STORE_URL',
  SHOPIFY_LINK_FAILURE: 'SHOPIFY_LINK_FAILURE',
  EDIT_PICKUP_ADDRESS: 'EDIT_PICKUP_ADDRESS',
  SAVE_PICKUP_ADDRESS: 'SAVE_PICKUP_ADDRESS',
  EDIT_ORDER_SYNC: 'EDIT_ORDER_SYNC',
  SAVE_ORDER_SYNC: 'SAVE_ORDER_SYNC',
  MANAGE_EMPLOYEES: 'MANAGE_EMPLOYEES',
  SET_UP_GFB_ACCOUNT: 'SET_UP_GFB_ACCOUNT',

  // EXPRESS_SHOPIFY_SETTINGS_UNLINK_WEB State
  UNLINK: 'UNLINK',
  CANCEL: 'CANCEL',

  // All CONTEXTUAL ONBOARDING / POPUP State
  EXIT: 'EXIT',
  LEAVE: 'LEAVE',

  // EXPRESS_SHOPIFY_VERIFY_ADDRESS_WEB State
  VERIFY: 'VERIFY',

  // additional to EXPRESS_BOOKINGS_WEB
  FILTER_SHOPIFY_ORDERS: 'FILTER_SHOPIFY_ORDERS',
  SHOPIFY_ORDER_NUMBER: 'SHOPIFY_ORDER_NUMBER',
  CANCEL_BOOKING: 'CANCEL_BOOKING',

  // EXPRESS_SHOPIFY_ORDER_NUMBER_ITEMS_WEB state
  CLOSE: 'CLOSE',

  // EXPRESS_SHOPIFY_WELCOME_GO_SETTINGS_WEB
  GO_TO_SETTINGS: 'GO_TO_SETTINGS',
  LATER: 'LATER',
};

export const SCRIBE_SAVED_POI_EVENTS = {
  DEFAULT: 'DEFAULT',
  SAVED_POI_BOOKMARK: 'SAVED_POI_BOOKMARK',
  EDIT_SAVED_POI_LABEL: 'EDIT_SAVED_POI_LABEL',
  EDIT_SAVED_POI_ADDRESS: 'EDIT_SAVED_POI_ADDRESS',
  EDIT_SAVED_POI_ADDRESS_DETAILS: 'EDIT_SAVED_POI_ADDRESS_DETAILS',
  EDIT_SAVED_POI_NOTE: 'EDIT_SAVED_POI_NOTE',
  EDIT_SAVED_POI_CONTACT_NAME: 'EDIT_SAVED_POI_CONTACT_NAME',
  EDIT_SAVED_POI_CONTACT_NUMBER: 'EDIT_SAVED_POI_CONTACT_NUMBER',
  SAVE: 'SAVE',
  CANCEL: 'CANCEL',
  REMOVE: 'REMOVE',
};

export const SCRIBE_SHOPIFY_PARAMS = {
  COMPANY_ID: 'COMPANY_ID',
  PAX_ID: 'PAX_ID',
  LOGIN_TYPE: 'LOGIN_TYPE',
  ORDER_ID: 'ORDER_ID',
  BOOKING_CODE: 'BOOKING_CODE',
  STEP_TYPE: 'STEP_TYPE',
  PREVIOUS_PICKUP_POI_ID: 'PREVIOUS_PICKUP_POI_ID',
  PICKUP_POI_ID: 'PICKUP_POI_ID',
  DROPOFF_POI_ID: 'DROPOFF_POI_ID',
  SERVICE_TYPE: 'SERVICE_TYPE',
  SERVICE_TYPE_LIST: 'SERVICE_TYPE_LIST',
  ONGOING_BOOKINGS: 'ONGOING_BOOKINGS',
  PAST_BOOKINGS: 'PAST_BOOKINGS',
  TAXI_TYPE: 'TAXI_TYPE ',
  TAXI_TYPE_LIST: 'TAXI_TYPE_LIST',
  FARE_LIST: 'FARE_LIST',
  FARE: 'FARE',
  CITY_ID: 'CITY_ID',
  BOOKING_MODE: 'BOOKING_MODE',
  SEARCH_TEXT: 'SEARCH_TEXT',
  COUNT_DROPOFFS: 'COUNT_DROPOFFS',
  DELIVERY_NUMBER: 'DELIVERY_NUMBER',
  RECIPIENT_NAME_TEXT: 'RECIPIENT_NAME_TEXT',
  RECIPIENT_PHONE_TEXT: 'RECIPIENT_PHONE_TEXT',
  COD_AMOUNT: 'COD_AMOUNT',
  ITEM_WEIGHT: 'ITEM_WEIGHT',
  CATEGORY_NAME: 'CATEGORY_NAME',
  TEXT: 'TEXT',
  IS_CASHLESS: 'IS_CASHLESS',
  PAYMENT_METHOD: 'PAYMENT_METHOD',
  TAG: 'TAG',
  HAS_PROMO_AVAILABLE: 'HAS_PROMO_AVAILABLE',
  HAS_PROMO_APPLIED: 'HAS_PROMO_APPLIED',
  TOTAL_DELIVERIES: 'TOTAL_DELIVERIES',
  INVALID_DELIVERIES: 'INVALID_DELIVERIES',
  CURRENCY: 'CURRENCY',
  BULK_DELIVERY_REFERENCE_CODE: 'BULK_DELIVERY_REFERENCE_CODE',
  SHOPIFY_STORE_NAME: 'SHOPIFY_STORE_NAME',
  SHOPIFY_STORE_ID: 'SHOPIFY_STORE_ID',
  SHOPIFY_STORE_ID_ALL: 'SHOPIFY_STORE_ID_ALL',
  TOTAL_STORES: 'TOTAL_STORES',
  SHOPIFY_ORDER_ID: 'SHOPIFY_ORDER_ID',
  SHOPIFY_ORDER_DATETIME: 'SHOPIFY_ORDER_DATETIME',
  TOTAL_ORDERS_ONGOING: 'TOTAL_ORDERS_ONGOING',
  TOTAL_PICKUP_ADDRESS: 'TOTAL_PICKUP_ADDRESS',
  ZOOM_MAGNITUDE: 'ZOOM_MAGNITUDE',
  TOTAL_BOOKED_ORDERS: 'TOTAL_BOOKED_ORDERS',
  IS_MULTISTOP_BOOKING: 'IS_MULTISTOP_BOOKING',
  IS_MULTISTOP_BOOKING_AVAILABLE: 'IS_MULTISTOP_BOOKING_AVAILABLE',
  MULTISTOP_UNAVAILABLE_ERROR_MESSAGE: 'MULTISTOP_UNAVAILABLE_ERROR_MESSAGE',
  PREVIOUS_STORE_URL: 'PREVIOUS_STORE_URL',
  NEW_STORE_URL: 'NEW_STORE_URL',
  PREVIOUS_DEFAULT_PRODUCT_CATEGORY: 'PREVIOUS_DEFAULT_PRODUCT_CATEGORY',
  NEW_DEFAULT_PRODUCT_CATEGORY: 'NEW_DEFAULT_PRODUCT_CATEGORY',
  PREVIOUS_DEFAULT_ITEM_WEIGHT: 'PREVIOUS_DEFAULT_ITEM_WEIGHT',
  NEW_DEFAULT_ITEM_WEIGHT: 'NEW_DEFAULT_ITEM_WEIGHT',
  PREVIOUS_ORDER_SYNC_STATUS: 'PREVIOUS_ORDER_SYNC_STATUS',
  NEW_ORDER_SYNC_STATUS: 'NEW_ORDER_SYNC_STATUS',
  TOTAL_ITEMS: 'TOTAL_ITEMS',
  TOTAL_PRICE: 'TOTAL_PRICE',
  SHIPPING_TYPE: 'SHIPPING_TYPE',
  SHIPPING_TYPE_FILTER: 'SHIPPING_TYPE_FILTER',
  PICKUP_NAME_FILTER: 'PICKUP_NAME_FILTER',
  PICKUP_POI_ID_FILTER: 'PICKUP_POI_ID_FILTER',
  IS_SHOPIFY_ORDER: 'IS_SHOPIFY_ORDER',
  ERROR_MESSAGE: 'ERROR_MESSAGE',
  START_DATE: 'START_DATE',
  END_DATE: 'END_DATE',
  DIRECTED_FROM_SHOPIFY_APPSTORE: 'DIRECTED_FROM_SHOPIFY_APPSTORE',
  CUSTOMER_NOTE: 'CUSTOMER_NOTE',
  LOCATION_DETAILS: 'LOCATION_DETAILS',
  IS_SHOPIFY_STORE_LINKED: 'IS_SHOPIFY_STORE_LINKED',
  TOTAL_COST_SAVED: 'TOTAL_COST_SAVED',
  TOTAL_RECIPIENTS: 'TOTAL_RECIPIENTS',
};

export const SCRIBE_MEX_APP_EVENTS = {
  DEFAULT: 'DEFAULT',
  GRAB_ACCOUNT_LOGIN: 'GRAB_ACCOUNT_LOGIN',
};

export const SCRIBE_MEX_APP_PARAMS = {
  MEX_PARENT_ENTITY_ID: 'MEX_PARENT_ENTITY_ID',
  MEX_GRABFOOD_ENTITY_ID: 'MEX_GRABFOOD_ENTITY_ID',
  MEX_USER_GRAB_ID: 'MEX_USER_GRAB_ID',
  MEX_SERVICE_ID: 'MEX_SERVICE_ID',
  MEX_ENABLE_TROY: 'MEX_ENABLE_TROY',
  MEX_TROY_WHITELIST: 'MEX_TROY_WHITELIST',
  MEX_TYPE: 'MEX_TYPE',
  MEX_USER_ROLE: 'MEX_USER_ROLE',
  MEX_USER_STATUS: 'MEX_USER_STATUS',
};

export const SCRIBE_EXPRESS_BULK_UPLOAD_ERROR_PAGE = {
  DEFAULT: 'DEFAULT',
  CONFIRM_INVALID_FIELD: 'CONFIRM_INVALID_FIELD',
};

export const SCRIBE_EXPRESS_BULK_UPLOAD_WEB_EVENTS = {
  DOWNLOAD_TEMPLATE: 'DOWNLOAD_TEMPLATE',
  UPLOAD: 'UPLOAD',
  BACK: 'BACK',
  SUCCESS_UPLOAD: 'SUCCESS_UPLOAD',
};

export const SCRIBE_EXPRESS_BULK_UPLOAD_REVIEW_WEB_EVENTS = {
  DEFAULT: 'DEFAULT',
  SELECT_ALL_ROWS: 'SELECT_ALL_ROWS',
  SELECT_ROWS_WITH_ERROR: 'SELECT_ROWS_WITH_ERROR',
  EDIT_ADDRESS: 'EDIT_ADDRESS',
  CONFIRM: 'CONFIRM',
};

/**
 * Inits the Scribe SDK if it is not initialized and return the instance
 */
export const getScribeSDK = () => {
  if (!window || !window.scribesdk)
    return {
      initialize: noop,
      sendEvent: noop,
      setAuthToken: noop,
      setFacets: noop,
    };

  if (!window.scribeInitDone) {
    window.scribesdk.initialize(
      SCRIBE_CONFIG.appName,
      SCRIBE_CONFIG.custType,
      isProduction(),
    );
    window.scribeInitDone = true;
  }

  return window.scribesdk;
};

export const scribeTrackEvent = ({
  stateName,
  eventName,
  facets,
  forceUpdate = false,
}) => {
  // eslint-disable-next-line no-console
  console.log('[SCRIBE DEBUG]', { stateName, eventName, facets });
  const scribeSDK = getScribeSDK();
  try {
    const scribePromise = scribeSDK.sendEvent(eventName, {
      ...facets,
      [SCRIBE_EVENT_PARAMS.PREVIOUS_STATE]: getPrevStateName(),
      [SCRIBE_EVENT_PARAMS.STATE_NAME]: stateName,
    });
    if (forceUpdate) {
      scribeSDK.forceUpdate();
    }
    if (!_isEmpty(stateName)) {
      savePrevStateName(stateName);
    }
    return scribePromise;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error({
      scribeTrackingErr: e,
    });
  }
  return Promise.resolve();
};

export const getPrevStateName = () =>
  Storage.getItem(SCRIBE_PREV_STATE_STORAGE_KEY) || '';

export const savePrevStateName = stateName =>
  Storage.setItem(SCRIBE_PREV_STATE_STORAGE_KEY, stateName);

export const getCommonMexAppFacet = (mexInfo = getMexInfo()) => ({
  [SCRIBE_MEX_APP_PARAMS.MEX_PARENT_ENTITY_ID]: _get(mexInfo, 'parentEntityID'),
  [SCRIBE_MEX_APP_PARAMS.MEX_GRABFOOD_ENTITY_ID]: _get(
    mexInfo,
    'grabfoodEntityID',
  ),
  [SCRIBE_MEX_APP_PARAMS.MEX_USER_GRAB_ID]: _get(mexInfo, 'userGrabID'),
  [SCRIBE_MEX_APP_PARAMS.MEX_SERVICE_ID]: _get(mexInfo, 'serviceID'),
  [SCRIBE_MEX_APP_PARAMS.MEX_ENABLE_TROY]: _get(mexInfo, 'enableTroy'),
  [SCRIBE_MEX_APP_PARAMS.MEX_TROY_WHITELIST]: _get(mexInfo, 'troyWhitelist'),
  [SCRIBE_MEX_APP_PARAMS.MEX_TYPE]: _get(mexInfo, 'mexType'),
  [SCRIBE_MEX_APP_PARAMS.MEX_USER_ROLE]: _get(mexInfo, 'userRole'),
  [SCRIBE_MEX_APP_PARAMS.MEX_USER_STATUS]: _get(mexInfo, 'userStatus'),
});

export const getCommonFacet = userInfo => {
  const cityID = _get(userInfo, 'selectedCity.cityID');
  const cityCode = _get(userInfo, 'selectedCity.cityCode');

  if (isPaxUser()) {
    return {
      [SCRIBE_EVENT_PARAMS.LOGIN_TYPE]: 'PAX_ID',
      [SCRIBE_EVENT_PARAMS.PAX_ID]: getPaxID(),
      [SCRIBE_EVENT_PARAMS.CITY_ID]: cityID,
      [SCRIBE_EVENT_PARAMS.CITY_CODE]: cityCode,
      ...(isFromMexApp() && getCommonMexAppFacet()),
    };
  }

  return {
    [SCRIBE_EVENT_PARAMS.LOGIN_TYPE]: 'GFB',
    [SCRIBE_EVENT_PARAMS.COMPANY_ID]: getPrevSelectedCompanyID(),
    [SCRIBE_EVENT_PARAMS.CITY_ID]: cityID,
    [SCRIBE_EVENT_PARAMS.CITY_CODE]: cityCode,
  };
};

export const scribeTrackOrderPageCreateBooking = (order, userInfo, mode) => {
  scribeTrackEvent({
    stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
    eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.BOOK,
    facets: {
      ...getCommonFacet(userInfo),
      [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: _get(order, 'senderLocation.id'),
      [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: _get(
        order,
        'recipientLocations',
        [],
      ).map(loc => loc.id),
      [SCRIBE_EVENT_PARAMS.SERVICE_TYPE]: _get(order, 'SERVICE_TYPE.value'),
      [SCRIBE_EVENT_PARAMS.TAXI_TYPE]: _get(order, 'SERVICE_TYPE.value'),
      [SCRIBE_EVENT_PARAMS.FARE]: _get(order, 'quote.forRender.amount'),
      [SCRIBE_EVENT_PARAMS.BOOKING_MODE]: mode,
      [SCRIBE_EVENT_PARAMS.RECIPIENT_PHONE_TEXT]: _get(
        order,
        'phone-recipient-0.value',
      ),
      [SCRIBE_EVENT_PARAMS.RECIPIENT_NAME_TEXT]: _get(
        order,
        'name-recipient-0.value',
      ),
      [SCRIBE_EVENT_PARAMS.COD_AMOUNT]: _get(order, 'cod-recipient-0.value', 0),
      [SCRIBE_EVENT_PARAMS.ITEM_WEIGHT]: _get(order, 'ITEM_WEIGHT.value'),
      [SCRIBE_EVENT_PARAMS.CATEGORY_NAME]: _get(order, 'ITEM_TYPE.value'),
      [SCRIBE_EVENT_PARAMS.TEXT]: _get(order, 'INSTRUCTION.value'),
      [SCRIBE_EVENT_PARAMS.PAYMENT_METHOD]: _get(order, 'PAYMENT_METHOD.value'),
      [SCRIBE_EVENT_PARAMS.PROMO_CODE]: _get(order, 'PROMO.value'),
      [SCRIBE_EVENT_PARAMS.HAS_PROMO_APPLIED]: _get(
        order,
        'quote.forRender.isDiscountApplied',
        false,
      ),
      [SCRIBE_EVENT_PARAMS.TOTAL_DELIVERIES]: 1,
      [SCRIBE_EVENT_PARAMS.CURRENCY]: _get(
        order,
        'quote.forRender.currency.code',
      ),
    },
  });
};

export const trackEVRegulationEvent = ({
  userInfo,
  errorCode,
  title,
  stateName,
  eventName,
}) => {
  let errorType;
  if (errorCode && isAreaZoningError(errorCode)) {
    errorType = isAreaZoningHardBlock(errorCode) ? 'HARD_BLOCK' : 'SOFT_BLOCK';
  }

  const commonFacets = getCommonFacet(userInfo || {});
  delete commonFacets[SCRIBE_EVENT_PARAMS.LOGIN_TYPE];

  scribeTrackEvent({
    stateName,
    eventName,
    facets: {
      ...commonFacets,
      ...(errorType && { [SCRIBE_EVENT_PARAMS.ERROR_TYPE]: errorType }),
      [SCRIBE_EVENT_PARAMS.TYPE_OF_CONTENT]: title,
    },
  });
};

export default {};
